import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import { formatarDataHora } from '../utils/formatadores.js';

export default function StandardFormDialog({
  open,
  onClose,
  title,
  tabs = [],
  onSave,
  loading = false,
  error = null,
  success = null,
  disableSave = false,
  maxWidth = 'lg',
  paperProps = {},
  onErrorClose,
  onSuccessClose,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [dataHoraAtual, setDataHoraAtual] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setDataHoraAtual(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (open) {
      setActiveTab(0);
    }
  }, [open]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSave = async () => {
    if (onSave) await onSave();
  };

  const handlePreviousTab = () => {
    setActiveTab(prev => Math.max(0, prev - 1));
  };

  const handleNextTab = () => {
    setActiveTab(prev => Math.min(tabs.length - 1, prev + 1));
  };

  /**
   * CORREÇÃO IMPORTANTE:
   * Removemos a largura fixa de 900px e agora usamos:
   * - width: '95vw'
   * - maxWidth: maxWidth (padrão do MUI)
   * Isso libera o Grid e faz o formulário ocupar toda a linha corretamente.
   */
  const defaultPaperProps = {
    sx: {
      width: '95vw',
      maxWidth: maxWidth,   // Agora respeita o valor passado ao componente
      height: '90vh',
      maxHeight: '90vh',
      position: 'relative',
      margin: 'auto'
    }
  };

  const mergedPaperProps = {
    ...defaultPaperProps,
    ...paperProps,
    sx: {
      ...defaultPaperProps.sx,
      ...(paperProps.sx || {})
    }
  };

  return (
    <>
      {/* Notificações */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={onErrorClose || (() => {})}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={onErrorClose || (() => {})} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={onSuccessClose || (() => {})}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={onSuccessClose || (() => {})} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Dialog */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={maxWidth}
        fullWidth
        disableEscapeKeyDown={false}
        PaperProps={mergedPaperProps}
      >
        {/* Header */}
        <DialogTitle sx={{ 
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderBottom: 1,
          borderColor: 'divider',
          padding: '16px',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          fontSize: '1.25rem',
          fontWeight: 500,
          lineHeight: 1.5
        }}>
          {title}
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ 
          width: '100%',
          maxWidth: '100%',
          height: 'calc(100% - 140px)',
          overflow: 'auto',
          padding: '16px',
          position: 'relative',
          marginBottom: '60px',

          '& .MuiInputLabel-root': { fontSize: '0.875rem' },
          '& .MuiInputBase-input': { fontSize: '0.875rem' },
          '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
        }}>
          {/* Tabs */}
          {tabs.length > 0 && (
            <>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  mb: 2
                }}
              >
                {tabs.map((tab, index) => (
                  <Tab key={index} label={tab.label} />
                ))}
              </Tabs>

              {/* Tab Content */}
              {tabs[activeTab] && tabs[activeTab].content}
            </>
          )}

          {/* Sem Tabs */}
          {tabs.length === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {tabs.content || null}
            </Box>
          )}
        </DialogContent>

        {/* Footer */}
        <DialogActions sx={{ 
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '0 16px',
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1,
          height: '60px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          {/* Data/Hora */}
          <Typography variant="body2" color="text.secondary">
            {formatarDataHora(dataHoraAtual)}
          </Typography>

          {/* Botões */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            
            {tabs.length > 1 && (
              <>
                <Button variant="outlined" onClick={handlePreviousTab} disabled={activeTab === 0}>
                  Anterior
                </Button>

                <Button variant="outlined" onClick={handleNextTab} disabled={activeTab === tabs.length - 1}>
                  Próximo
                </Button>
              </>
            )}

            <Button
              variant="contained"
              onClick={onClose}
            >
              FECHAR
            </Button>

            {onSave && (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading || disableSave}
              >
                {loading ? <CircularProgress size={24} /> : "SALVAR"}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}
